import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AdapterDateFns,
  Box,
  DateRange,
  DateRangeCalendar,
  List,
  ListItem,
  LocalizationProvider,
  Popover,
} from '@hopskipdrive/react-components';
import { ArrowDropDown } from '@mui/icons-material';
import { endOfISOWeek, endOfMonth, format, startOfISOWeek, startOfMonth, subDays, subMonths } from 'date-fns';

import { FilterButton } from '../FilterPanel.styles';

import { StyledListItemSx, StyledShortcutListSx } from './DateRangeFilter.styles';

export const DateRangeFilter = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const [dateRange, setDateRange] = useState<DateRange<Date>>([null, null]);
  const isDateRangeSet = dateRange[0] !== null && dateRange[1] !== null;

  useEffect(() => {
    const startDate = searchParams.get('startDate');
    const endDate = searchParams.get('endDate');

    if (startDate && endDate) {
      setDateRange([new Date(startDate), new Date(endDate)]);
    } else {
      setDateRange([null, null]);
    }
  }, [searchParams]);
  const handleOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);

    if (newRange[0] && newRange[1]) {
      const newParams = new URLSearchParams(searchParams);

      if (searchParams.get('page')) {
        newParams.set('page', '1');
      }

      newParams.set('startDate', format(newRange[0], 'M/d/yyyy'));
      newParams.set('endDate', format(newRange[1], 'M/d/yyyy'));

      setSearchParams(newParams);
      handleClose();
    }
  };

  const setToday = () => {
    const today = new Date();

    handleDateRangeChange([today, today]);
    handleClose();
  };
  const setThisWeek = () => {
    const today = new Date();

    handleDateRangeChange([startOfISOWeek(today), endOfISOWeek(today)]);
    handleClose();
  };
  const setLastWeek = () => {
    const today = new Date();

    handleDateRangeChange([startOfISOWeek(subDays(today, 7)), endOfISOWeek(subDays(today, 7))]);
    handleClose();
  };
  const setThisMonth = () => {
    const today = new Date();

    handleDateRangeChange([startOfMonth(today), endOfMonth(today)]);
    handleClose();
  };
  const setLastMonth = () => {
    const today = new Date();

    handleDateRangeChange([startOfMonth(subMonths(today, 1)), endOfMonth(subMonths(today, 1))]);
    handleClose();
  };

  const open = Boolean(anchorEl);
  const id = open ? 'date-range-calendar-popover' : undefined;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <FilterButton variant="text" open={Boolean(anchorEl)} active={isDateRangeSet} onClick={handleOpen}>
        Date Range
        <ArrowDropDown />
      </FilterButton>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '.MuiBackdrop-root': { backgroundColor: 'transparent' },
        }}
        PaperProps={{
          sx: {
            mt: '16px',
          },
        }}
      >
        <Box display="flex" flexDirection="row">
          <List sx={StyledShortcutListSx}>
            <ListItem sx={StyledListItemSx} onClick={setToday}>
              Today
            </ListItem>
            <ListItem sx={StyledListItemSx} onClick={setThisWeek}>
              This Week
            </ListItem>
            <ListItem sx={StyledListItemSx} onClick={setLastWeek}>
              Last Week
            </ListItem>
            <ListItem sx={StyledListItemSx} onClick={setThisMonth}>
              This Month
            </ListItem>
            <ListItem sx={StyledListItemSx} onClick={setLastMonth}>
              Last Month
            </ListItem>
          </List>
          <DateRangeCalendar value={dateRange} onChange={handleDateRangeChange} />
        </Box>
      </Popover>
    </LocalizationProvider>
  );
};
